import { computed, MaybeRef, unref } from 'vue'
import { ApiUrls, EndpointResponse } from './types/base'
import { TableStats } from './types/stats'
import { Filters } from './types/types'
import usePejerQuery from './usePejerQuery'
import { getQueryStringFromFiltersObject } from './utils'

export type PejerGraphProviderOptions<
  T extends ApiUrls<any, any>,
  K extends TableStats<L>,
  L extends Record<string, string | number>,
  J,
> = {
  url: T
  query?: MaybeRef<Filters<J>>
  groupBy: 'day' | 'month' | 'year' | 'none'
  fromDate?: MaybeRef<Date | undefined>
  toDate?: MaybeRef<Date | undefined>
}

export function usePejerGraphProvider<
  T extends ApiUrls<any, any>,
  //   FUNCTION extends (data: K[], query?: unknown) => unknown,
  K extends TableStats<L>,
  L extends Record<string, string | number>,
  J,
>(options: PejerGraphProviderOptions<T, K, L, J>) {
  type EndpointResponseItem = EndpointResponse<T>[number]

  const query = computed(() =>
    options.query ? getQueryStringFromFiltersObject(unref(options.query)) : ''
  )

  const fromDate = computed(() =>
    options.fromDate ? unref(options.fromDate)?.toISOString().slice(0, 10) : undefined
  )
  const toDate = computed(() =>
    options.toDate ? unref(options.toDate)?.toISOString().slice(0, 10) : undefined
  )

  const { data, dataState } = usePejerQuery<T, EndpointResponseItem[]>(options.url, {
    queryKey: [options.url, query, options.groupBy, fromDate, toDate],
    body: {
      filters: query,
      groupBy: options.groupBy,
      fromDate: fromDate,
      toDate: toDate,
    },
    select: (data) => data.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  return {
    data,
    dataState,
  }
}
